export type Locale = 'pl' | 'en' | 'de';

interface PlaygroundCopy {
  label: string;
  title: string;
  subtitle: string;
  field_age: string;
  field_sex: string;
  field_experience: string;
  field_goal: string;
  field_days: string;
  field_duration: string;
  field_equipment: string;
  field_injuries: string;
  field_injuries_hint: string;
  injuries_placeholder: string;
  sex_male: string;
  sex_female: string;
  sex_other: string;
  exp_beginner: string;
  exp_intermediate: string;
  exp_advanced: string;
  goal_fat_loss: string;
  goal_muscle_gain: string;
  goal_strength: string;
  goal_endurance: string;
  goal_general_fitness: string;
  eq_barbell: string;
  eq_dumbbells: string;
  eq_cables: string;
  eq_machines: string;
  eq_bands: string;
  eq_bodyweight: string;
  generate: string;
  generating: string;
  regenerate: string;
  disclaimer: string;
  summary_weeks: string;
  summary_split: string;
  summary_exercises: string;
  summary_excluded: string;
  summary_excluded_hint: string;
  preview_title: string;
  week_label: string;
  day_label: string;
  deload_badge: string;
  trace_title: string;
  trace_subtitle: string;
  cta_title: string;
  cta_body: string;
  cta_button: string;
}

interface Copy {
  disclaimer: string;
  headline: string;
  subhead: string;
  cta_signup: string;
  cta_pricing: string;
  cta_try_now: string;
  login: string;
  f1_title: string;
  f1_body: string;
  f2_title: string;
  f2_body: string;
  f3_title: string;
  f3_body: string;
  pricing: string;
  tier_starter: string;
  tier_pro: string;
  tier_studio: string;
  price_starter: string;
  price_pro: string;
  price_studio: string;
  features_starter: string[];
  features_pro: string[];
  features_studio: string[];
  privacy: string;
  terms: string;
}

export const copy: Record<Locale, Copy> = {
  pl: {
    disclaimer: 'Platforma wspiera trening i kondycję. Nie zastępuje konsultacji u specjalisty.',
    headline: 'Inteligentny coaching dla trenerów personalnych',
    subhead:
      'Generuj spersonalizowane plany w 30 sekund. Hostowane w EU. Każdy plan ma pełen ślad decyzji.',
    cta_signup: 'Poproś o dostęp',
    cta_pricing: 'Zobacz cennik',
    cta_try_now: 'Wygeneruj plan teraz',
    login: 'Logowanie',
    f1_title: 'Plany w 30 sekund',
    f1_body: 'Generator oparty o reguły treningowe. Bez czarnej skrzynki.',
    f2_title: 'EU + GDPR',
    f2_body: 'Dane hostowane we Frankfurcie. Zero przepływu danych zdrowotnych do AI.',
    f3_title: 'Postępy klientów',
    f3_body: 'Logi, pomiary, raporty. Wszystko w jednym panelu.',
    pricing: 'Cennik',
    tier_starter: 'Starter',
    tier_pro: 'Pro',
    tier_studio: 'Studio',
    price_starter: 'Darmowy',
    price_pro: '€30 / mies.',
    price_studio: '€80 / mies.',
    features_starter: ['Do 3 klientów', 'Podstawowe plany', 'Aplikacja klienta'],
    features_pro: [
      'Do 30 klientów',
      'Generator planów',
      'Asystent trenera (AI)',
      'Analityka',
      'Kalendarz',
    ],
    features_studio: [
      'Bez limitu',
      'Wielu trenerów',
      'Własny branding',
      'API + webhooks',
      'Dedykowany opiekun',
    ],
    privacy: 'Prywatność',
    terms: 'Regulamin',
  },
  en: {
    disclaimer: 'Platform supports training and conditioning. Does not replace consultation with a qualified professional.',
    headline: 'Smart coaching for personal trainers',
    subhead:
      'Generate personalized plans in 30 seconds. EU-hosted. Every plan ships with a full decision trace.',
    cta_signup: 'Request access',
    cta_pricing: 'See pricing',
    cta_try_now: 'Generate a plan now',
    login: 'Sign in',
    f1_title: 'Plans in 30 seconds',
    f1_body: 'Rule-based generator. No black box.',
    f2_title: 'EU + GDPR',
    f2_body: 'Hosted in Frankfurt. Zero health-data flow to AI.',
    f3_title: 'Client progress',
    f3_body: 'Logs, measurements, reports. One dashboard.',
    pricing: 'Pricing',
    tier_starter: 'Starter',
    tier_pro: 'Pro',
    tier_studio: 'Studio',
    price_starter: 'Free',
    price_pro: '€30 / mo',
    price_studio: '€80 / mo',
    features_starter: ['Up to 3 clients', 'Basic plans', 'Client app'],
    features_pro: ['Up to 30 clients', 'Plan generator', 'Trainer assistant (AI)', 'Analytics', 'Calendar'],
    features_studio: [
      'Unlimited',
      'Multi-trainer',
      'Custom branding',
      'API + webhooks',
      'Dedicated CSM',
    ],
    privacy: 'Privacy',
    terms: 'Terms',
  },
  de: {
    disclaimer:
      'Plattform unterstützt Training und Kondition. Ersetzt keine Beratung durch qualifizierte Fachpersonen.',
    headline: 'Smartes Coaching für Personal Trainer',
    subhead:
      'Generiere personalisierte Pläne in 30 Sekunden. EU-gehostet. Jeder Plan kommt mit voller Entscheidungsspur.',
    cta_signup: 'Zugang anfordern',
    cta_pricing: 'Preise ansehen',
    cta_try_now: 'Jetzt Plan generieren',
    login: 'Anmelden',
    f1_title: 'Pläne in 30 Sekunden',
    f1_body: 'Regelbasierter Generator. Keine Black-Box.',
    f2_title: 'EU + DSGVO',
    f2_body: 'In Frankfurt gehostet. Null Fluss von Gesundheitsdaten zu KI.',
    f3_title: 'Klientenfortschritt',
    f3_body: 'Logs, Messungen, Berichte. Ein Dashboard.',
    pricing: 'Preise',
    tier_starter: 'Starter',
    tier_pro: 'Pro',
    tier_studio: 'Studio',
    price_starter: 'Kostenlos',
    price_pro: '€30 / Mon.',
    price_studio: '€80 / Mon.',
    features_starter: ['Bis zu 3 Klienten', 'Basis-Pläne', 'Klienten-App'],
    features_pro: [
      'Bis zu 30 Klienten',
      'Plangenerator',
      'Trainer-Assistent (KI)',
      'Analysen',
      'Kalender',
    ],
    features_studio: [
      'Unbegrenzt',
      'Mehrere Trainer',
      'Eigenes Branding',
      'API + Webhooks',
      'Dedizierter Ansprechpartner',
    ],
    privacy: 'Datenschutz',
    terms: 'AGB',
  },
};

export const playgroundCopy: Record<Locale, PlaygroundCopy> = {
  pl: {
    label: 'Demo na żywo',
    title: 'Wygeneruj plan teraz — w 30 sekund',
    subtitle:
      'Wypełnij krótki formularz. Plan + ślad decyzji zobaczysz od razu, bez konta i bez czekania.',
    field_age: 'Wiek',
    field_sex: 'Płeć',
    field_experience: 'Doświadczenie',
    field_goal: 'Główny cel',
    field_days: 'Dni treningowe w tygodniu',
    field_duration: 'Długość sesji (minuty)',
    field_equipment: 'Dostępny sprzęt',
    field_injuries: 'Kontuzje lub ograniczenia',
    field_injuries_hint: 'Oddziel przecinkami. Np. „dolny odcinek pleców, kolano".',
    injuries_placeholder: 'np. lower back, kolano…',
    sex_male: 'M',
    sex_female: 'K',
    sex_other: 'Inna',
    exp_beginner: 'Początkujący',
    exp_intermediate: 'Średni',
    exp_advanced: 'Zaawansowany',
    goal_fat_loss: 'Redukcja',
    goal_muscle_gain: 'Masa',
    goal_strength: 'Siła',
    goal_endurance: 'Wytrzymałość',
    goal_general_fitness: 'Ogólna forma',
    eq_barbell: 'Sztanga',
    eq_dumbbells: 'Hantle',
    eq_cables: 'Wyciągi',
    eq_machines: 'Maszyny',
    eq_bands: 'Gumy',
    eq_bodyweight: 'Masa ciała',
    generate: 'Wygeneruj plan',
    generating: 'Generowanie…',
    regenerate: 'Wygeneruj ponownie',
    disclaimer: 'Demo. Prawdziwe plany generuje trener w aplikacji — z pełną ankietą i zgodą.',
    summary_weeks: 'Tygodnie',
    summary_split: 'Podział',
    summary_exercises: 'Ćwiczeń',
    summary_excluded: 'Wykluczonych',
    summary_excluded_hint: 'przez kontuzje / brak sprzętu',
    preview_title: 'Twój plan',
    week_label: 'Tydzień {n}',
    day_label: 'Dzień {n}',
    deload_badge: 'Deload',
    trace_title: 'Ślad decyzji',
    trace_subtitle: 'Które reguły zadziałały i dlaczego — pełna transparencja.',
    cta_title: 'Podoba się?',
    cta_body: 'Załóż konto i twórz plany dla swoich klientów. Starter za darmo, do 3 klientów.',
    cta_button: 'Załóż konto trenera',
  },
  en: {
    label: 'Live demo',
    title: 'Generate a plan now — in 30 seconds',
    subtitle:
      'Fill in a short form. You see the plan + decision trace instantly. No signup, no waiting.',
    field_age: 'Age',
    field_sex: 'Sex',
    field_experience: 'Experience',
    field_goal: 'Primary goal',
    field_days: 'Training days / week',
    field_duration: 'Session length (minutes)',
    field_equipment: 'Available equipment',
    field_injuries: 'Injuries or limitations',
    field_injuries_hint: 'Comma-separated. E.g. "lower back, knee".',
    injuries_placeholder: 'e.g. lower back, knee…',
    sex_male: 'M',
    sex_female: 'F',
    sex_other: 'Other',
    exp_beginner: 'Beginner',
    exp_intermediate: 'Intermediate',
    exp_advanced: 'Advanced',
    goal_fat_loss: 'Fat loss',
    goal_muscle_gain: 'Muscle',
    goal_strength: 'Strength',
    goal_endurance: 'Endurance',
    goal_general_fitness: 'General fitness',
    eq_barbell: 'Barbell',
    eq_dumbbells: 'Dumbbells',
    eq_cables: 'Cables',
    eq_machines: 'Machines',
    eq_bands: 'Bands',
    eq_bodyweight: 'Bodyweight',
    generate: 'Generate plan',
    generating: 'Generating…',
    regenerate: 'Regenerate',
    disclaimer: 'Demo. Real plans are generated by trainers in-app after intake + consent.',
    summary_weeks: 'Weeks',
    summary_split: 'Split',
    summary_exercises: 'Exercises',
    summary_excluded: 'Excluded',
    summary_excluded_hint: 'by injuries / equipment',
    preview_title: 'Your plan',
    week_label: 'Week {n}',
    day_label: 'Day {n}',
    deload_badge: 'Deload',
    trace_title: 'Decision trace',
    trace_subtitle: 'Which rules fired and why — full transparency.',
    cta_title: 'Like it?',
    cta_body: 'Sign up and build plans for your clients. Starter is free, up to 3 clients.',
    cta_button: 'Create trainer account',
  },
  de: {
    label: 'Live-Demo',
    title: 'Plan jetzt generieren — in 30 Sekunden',
    subtitle:
      'Fülle ein kurzes Formular aus. Plan + Entscheidungsspur siehst du sofort. Ohne Konto.',
    field_age: 'Alter',
    field_sex: 'Geschlecht',
    field_experience: 'Erfahrung',
    field_goal: 'Hauptziel',
    field_days: 'Trainingstage / Woche',
    field_duration: 'Session-Länge (Minuten)',
    field_equipment: 'Equipment',
    field_injuries: 'Verletzungen / Einschränkungen',
    field_injuries_hint: 'Komma-getrennt. Z.B. „unterer Rücken, Knie".',
    injuries_placeholder: 'z.B. unterer Rücken, Knie…',
    sex_male: 'M',
    sex_female: 'W',
    sex_other: 'Andere',
    exp_beginner: 'Anfänger',
    exp_intermediate: 'Fortgeschritten',
    exp_advanced: 'Sehr fortgeschritten',
    goal_fat_loss: 'Fettabbau',
    goal_muscle_gain: 'Muskel',
    goal_strength: 'Kraft',
    goal_endurance: 'Ausdauer',
    goal_general_fitness: 'Allg. Fitness',
    eq_barbell: 'Langhantel',
    eq_dumbbells: 'Kurzhanteln',
    eq_cables: 'Kabel',
    eq_machines: 'Maschinen',
    eq_bands: 'Bänder',
    eq_bodyweight: 'Eigengewicht',
    generate: 'Plan generieren',
    generating: 'Wird generiert…',
    regenerate: 'Neu generieren',
    disclaimer: 'Demo. Echte Pläne werden von Trainern in-app nach Fragebogen + Einwilligung erstellt.',
    summary_weeks: 'Wochen',
    summary_split: 'Split',
    summary_exercises: 'Übungen',
    summary_excluded: 'Ausgeschlossen',
    summary_excluded_hint: 'durch Verletzungen / Equipment',
    preview_title: 'Dein Plan',
    week_label: 'Woche {n}',
    day_label: 'Tag {n}',
    deload_badge: 'Deload',
    trace_title: 'Entscheidungsspur',
    trace_subtitle: 'Welche Regeln gegriffen haben und warum — volle Transparenz.',
    cta_title: 'Gefällt dir?',
    cta_body: 'Registriere dich und erstelle Pläne für deine Klienten. Starter kostenlos, bis 3 Klienten.',
    cta_button: 'Trainer-Konto erstellen',
  },
};
